import React, { useEffect } from 'react';
import ProForm, { ProFormSwitch, ProFormText } from '@ant-design/pro-form';
import { Alert, DatePicker, Form, Space, Typography } from 'antd';
import { useIntl, useSelector } from 'umi';
import FORM_TYPES from '@/constants/formTypes';
import { getFormBlurProps } from '@/utils/form';
import * as PromotionSelectors from '@/selectors/promotionSelectors';

import { PromotionModel } from '@/typings/models/Promotion';
import pick from 'lodash/pick';
import dayjs from 'dayjs';

interface PromotionDetailsFormProps {
  promotion: PromotionModel,
  isNew: boolean,
  onChange?: (promotion: PromotionModel) => void,
}

const PromotionDetailsForm: React.FC<PromotionDetailsFormProps> = ({
                                                                     promotion, isNew = false,
                                                                     onChange,
                                                                   }) => {

  const [form] = Form.useForm()


  const intl = useIntl();

  const loadings = useSelector(PromotionSelectors.selectPromotionChangeLoadings);
  const length = useSelector(PromotionSelectors.selectPromotionListLength);

  useEffect(() => {
    if (isNew) return
    const formValue = pick(promotion, ['title', 'is_active'])
    const formValues = {
      ...formValue,
      range: [
        promotion.start_date ? dayjs(promotion.start_date) : null,
        promotion.end_date ? dayjs(promotion.end_date) : null
      ]
    }
    form.setFieldsValue(formValues)
    // Notify parent of initial values so form state is properly initialized
    // Use setTimeout to ensure form values are set before calling onChange
    setTimeout(() => {
      const currentFormValues = form.getFieldsValue()
      onChange && onChange(currentFormValues)
    }, 0)

  }, [JSON.stringify(promotion)])


  function onFormChange(changedValues = null, values = form.getFieldsValue()) {
    onChange && onChange(values)
  }

  return (
    <div>
      <ProForm
        form={form}
        submitter={false}
        initialValues={promotion}
        onValuesChange={onFormChange}
      >
        {promotion.notice && (
          <Alert
            style={{ marginBottom: 24 }}
            type={'error'}
            message={
              <Space direction={'vertical'}>
                <Typography.Title level={5}>{promotion.notice}</Typography.Title>
              </Space>
            }
          />
        )}
        <ProFormText hidden name={"id"}/>
        <ProFormText
          label={intl.formatMessage({
            id: 'pages.promotions.title',
            defaultMessage: 'Title',
          })}
          required
          validateStatus={'validating'}
          hasFeedback
          initialValue={`Promotion #${length + 1}`}
          {...getFormBlurProps({
            type: FORM_TYPES.TEXT,
            value: promotion.title,
            name: 'title',
            loadings,
          })}
        />
        <ProForm.Item
          name={'range'}
          label={intl.formatMessage({
            id: 'pages.promotions.dates',
            defaultMessage: 'Dates',
          })}
        >
          <DatePicker.RangePicker/>
        </ProForm.Item>
        {!isNew && (
          <ProFormText
            disabled
            name="created"
            label={intl.formatMessage({
              id: 'pages.location.createDate',
              defaultMessage: 'Create Date',
            })}
          />
        )}
        <ProFormSwitch
          initialValue={true}
          checkedChildren={intl.formatMessage({
            id: 'common.active',
            defaultMessage: 'Active',
          })}
          unCheckedChildren={intl.formatMessage({
            id: 'common.inactive',
            defaultMessage: 'Inactive',
          })}
          label={intl.formatMessage({
            id: 'common.active',
            defaultMessage: 'Active',
          })}
          {...getFormBlurProps({
            type: FORM_TYPES.SWITCH,
            name: 'is_active',
            loadings,
          })}
        />
      </ProForm>
    </div>
  );
}

export default PromotionDetailsForm
